import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { UUID } from "@/lib/types";

/**
 * Opening a row's detail = you've seen it. Marks ALL of that row's notifications
 * read from whatever surface opened the panel (board, kanban, deep link, bell,
 * Postfach), so "unread" means one thing everywhere: you haven't opened the
 * ticket since its last update. The single chokepoint for read-state.
 */
export function useMarkRowSeen(rowId: UUID | undefined): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!rowId) return;
    let cancelled = false;
    void api
      .markRowNotificationsRead(rowId)
      .then((res) => {
        // Only refetch when something actually changed.
        if (cancelled || !res || res.marked <= 0) return;
        void qc.invalidateQueries({ queryKey: queryKeys.notifications });
        void qc.invalidateQueries({ queryKey: queryKeys.notificationsUnread });
        void qc.invalidateQueries({ queryKey: queryKeys.mySubmissions });
      })
      .catch(() => {
        /* best-effort: a failed read-mark shouldn't break opening the row */
      });
    return () => {
      cancelled = true;
    };
  }, [rowId, qc]);
}
